import { fmt } from "../components/ui-kit";
import React, { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { StatusBadge } from "../components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { RefreshCw, Info, Download, Search } from "lucide-react";
import { toast } from "sonner";

export default function DeliveryStatus() {
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState([]);
  const [retryFor, setRetryFor] = useState(null);
  const [pw, setPw] = useState("");
  const [q, setQ] = useState("");

  const download = async (r) => {
    try {
      const res = await api.get(`/delivery/${r.id}/download`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = r.filenames?.[0] || "document.pdf"; a.click();
    } catch (e) { toast.error("Download unavailable — document may have been purged."); }
  };

  const load = () => {
    api.get("/delivery", { params: q ? { q } : {} }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const retry = async () => {
    try {
      await api.post(`/delivery/${retryFor}/retry`, { password: pw });
      toast.success("Re-delivery attempted");
      setRetryFor(null); setPw(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Delivery Status</h1>
        <p className="mt-1 text-muted-foreground">
          Search by tracking number (Agency_Client name). Re-download any document at any time.
        </p>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="delivery-search" placeholder="Search tracking number…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9" />
      </div>

      <div className="flex items-start gap-2 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        Airfistuus keeps the permanent record. Your delivery inbox or Drive folder holds an optional copy.
      </div>

      <Tabs defaultValue="delivery">
        <TabsList>
          <TabsTrigger value="delivery" data-testid="tab-delivery">Deliveries</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="delivery" className="space-y-3">
          {rows.length === 0 && <p className="text-sm text-muted-foreground">No deliveries yet.</p>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`delivery-row-${r.id}`}
              className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="break-all font-mono text-sm font-bold">{r.tracking_id}</span>
                  <StatusBadge status={r.delivery_status} />
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {r.document_type} · {r.client_name || "purged"} · to {r.recipient}{r.cc ? ` (cc ${r.cc})` : ""}{r.sent_from ? ` · from ${r.sent_from}` : ""}
                </p>
                <p className="mt-0.5 font-mono text-xs text-muted-foreground">{r.filenames?.join(" · ")} · attempts {r.attempts}</p>
                {r.last_error && <p className="mt-1 text-xs text-destructive">Last error: {r.last_error}</p>}
              </div>
              <div className="flex gap-2">
              {!r.purged && (
                <Button size="sm" variant="outline" onClick={() => download(r)} data-testid={`download-${r.id}`}><Download className="mr-1.5 h-4 w-4" /> PDF</Button>
              )}
              {r.delivery_status === "delivery_failed" && !r.purged && (
                <Button size="sm" variant="outline" onClick={() => setRetryFor(r.id)}
                  data-testid={`retry-${r.id}`}>
                  <RefreshCw className="mr-1.5 h-4 w-4" /> Retry
                </Button>
              )}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="audit" className="space-y-2">
          {audit.map((e, i) => (
            <div key={i} data-testid={`audit-row-${i}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-2 text-sm">
              <span className="font-semibold">{e.action.replace(/_/g, " ")}</span>
              <span className="text-muted-foreground">{e.actor} · {fmt(e.at)}</span>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!retryFor} onOpenChange={(o) => !o && setRetryFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reauthenticate to retry delivery</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Confirm your password to re-attempt delivery for {retryFor}.</p>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="retry-password" />
          </div>
          <DialogFooter>
            <Button onClick={retry} data-testid="retry-confirm">Reauthenticate & retry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
